import React from 'react';
import {useTranslation} from 'react-i18next';
import {isAmount, isFieldDiff, isCampaignList, isPlainObject} from '../shapes';
import {humanizeFieldLabel, humanizeValue, isNumeric, formatNumber} from '../humanize';
import {HIDDEN_FIELDS, LEVEL_BADGES} from '../labels';
import {LevelBadge} from './LevelBadge';

const MAX_LIST_ITEMS = 6;
const MAX_INLINE_PAIRS = 6;

function CampaignList({campaigns, t}) {
    const shown = campaigns.slice(0, MAX_LIST_ITEMS);
    const rest = campaigns.length - shown.length;
    return (
        <ul className="activity-log-detail-campaigns">
            {shown.map((c, i) => (
                <li key={i}>
                    <strong>{c.name}</strong>
                    {c.converted ? ` — ${c.converted}` : (c.daily_budget ? ` — ${c.daily_budget}` : '')}
                </li>
            ))}
            {rest > 0 && <li>{t('activity_log.detail.more_items', {count: rest})}</li>}
        </ul>
    );
}

// Objeto anidado → pares "label: valor" inline, un solo nivel. Lo irreducible se omite:
// NUNCA JSON.stringify a pantalla.
function inlinePairs(value, t) {
    const pairs = Object.entries(value)
        .filter(([k, v]) => !HIDDEN_FIELDS.has(k) && v !== null && v !== undefined && v !== '' && typeof v !== 'object')
        .slice(0, MAX_INLINE_PAIRS)
        .map(([k, v]) => `${humanizeFieldLabel(k, t)}: ${humanizeValue(k, v, t)}`);
    return pairs.length ? pairs.join(' · ') : null;
}

function renderValue(field, value, t) {
    if (field === 'level' && LEVEL_BADGES[value]) return <LevelBadge level={value}/>;
    if (isCampaignList(value)) return <CampaignList campaigns={value} t={t}/>;
    if (isFieldDiff(value)) {
        return (
            <span>
                <span className="ald-diff-before">{humanizeValue(field, value.before, t) ?? '—'}</span>
                <span className="ald-diff-arrow" aria-hidden="true"> → </span>
                <span className="ald-diff-after">{humanizeValue(field, value.after, t) ?? '—'}</span>
            </span>
        );
    }
    const humanized = humanizeValue(field, value, t);
    if (humanized !== null) return humanized;
    if (Array.isArray(value)) {
        const primitives = value.filter(v => v !== null && v !== undefined && typeof v !== 'object');
        if (!primitives.length) return null;
        const shown = primitives.slice(0, MAX_LIST_ITEMS).map(v => (isNumeric(v) ? formatNumber(v) : String(v)));
        const rest = primitives.length - shown.length;
        return rest > 0 ? `${shown.join(', ')} ${t('activity_log.detail.more_items', {count: rest})}` : shown.join(', ');
    }
    if (isPlainObject(value) && !isAmount(value)) return inlinePairs(value, t);
    return null;
}

// Espejo SIN JSX de renderValue: ¿esta celda produciría algo visible? La usa el registry
// para decidir si la fila muestra "Ver detalle" — un toggle que abre un panel vacío es
// peor que no tener toggle.
function isRenderableValue(field, value) {
    if (value === null || value === undefined || value === '') return false;
    if (typeof value !== 'object') return true;
    if (isAmount(value)) return value.amount !== null && value.amount !== undefined && value.amount !== '';
    if (isCampaignList(value)) return true;
    if (isFieldDiff(value)) return true;
    if (Array.isArray(value)) return value.some(v => v !== null && v !== undefined && typeof v !== 'object');
    return Object.entries(value).some(([k, v]) =>
        !HIDDEN_FIELDS.has(k) && v !== null && v !== undefined && v !== '' && typeof v !== 'object');
}

export function hasRenderableRows(detail) {
    if (!isPlainObject(detail)) return false;
    return Object.entries(detail).some(([field, value]) =>
        !HIDDEN_FIELDS.has(field) && isRenderableValue(field, value));
}

// EL fallback universal del detalle: cualquier payload se muestra como tabla label/valor
// humanizada. Cubre eventos sin renderer registrado, shapes históricos y payloads
// imprevistos (ej. GOAL_MODIFIED con request.data crudo). Reemplaza para siempre al
// <pre> de JSON: acá no entra ni una llave.
export function GenericDetailTable({detail}) {
    const {t} = useTranslation();
    if (!isPlainObject(detail)) return null;

    const rows = Object.entries(detail)
        .filter(([field, value]) => !HIDDEN_FIELDS.has(field) && value !== null && value !== undefined && value !== '')
        .map(([field, value]) => [field, renderValue(field, value, t)])
        .filter(([, rendered]) => rendered !== null && rendered !== undefined && rendered !== '');
    if (!rows.length) return null;

    return (
        <table className="activity-log-detail-table">
            <tbody>
            {rows.map(([field, rendered]) => (
                <tr key={field}>
                    <td className="field-label">{humanizeFieldLabel(field, t)}</td>
                    <td className="field-after">{rendered}</td>
                </tr>
            ))}
            </tbody>
        </table>
    );
}
